import { useEffect, useMemo, useState, type FormEvent } from "react";

import { PatientUhidAutocomplete } from "../../components/ui/PatientUhidAutocomplete";
import { SearchableSelect } from "../../components/ui/SearchableSelect";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/Feedback";
import { IconPill } from "../../components/layout/icons";
import { ModuleHero } from "../../components/ui/ModuleHero";
import { ApiError } from "../../lib/api-client";
import { formatDate } from "../../lib/datetime";
import { usePatientGlobalSearch } from "../../lib/patient-uhid-search";
import { useToast } from "../../lib/toast";
import { usePatientGlobalLookup } from "../patients/patient.queries";
import { useSettingsList } from "../settings/settings.queries";
import type { StoreLookup } from "./store.api";
import { useCreateStoreRecord, useStoreLookup, useStoreRows } from "./store.queries";
import "./medicine-billing.css";

type BillLine = {
  key: number;
  category: string;
  item: string;
  batch: string;
  mrp: string;
  unitQuantity: string;
  subUnitQuantity: string;
  discountPercent: string;
};

type PaymentLine = {
  key: number;
  date: string;
  mode: "Cash" | "UPI" | "Transfer to Bank Account" | "Cheque" | "Online" | "Other";
  bankName: string;
  amount: string;
};

let lineKey = 0;
const localDateTime = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
};
const blankLine = (): BillLine => ({
  key: ++lineKey,
  category: "",
  item: "",
  batch: "",
  mrp: "0",
  unitQuantity: "0",
  subUnitQuantity: "0",
  discountPercent: "0",
});
const blankPayment = (): PaymentLine => ({
  key: ++lineKey,
  date: localDateTime(),
  mode: "Cash",
  bankName: "",
  amount: "0",
});
const num = (value: string | number | undefined) => Number(value ?? 0) || 0;
const medicinePrice = (batch: StoreLookup | undefined, item: StoreLookup | undefined) => {
  const batchMrp = num(batch?.mrp);
  const batchRate = num(batch?.rate);
  const itemMrp = num(item?.unitMrp);
  const itemRate = num(item?.unitRate);
  return batchMrp > 0 ? batchMrp : batchRate > 0 ? batchRate : itemMrp > 0 ? itemMrp : itemRate;
};
const options = (rows: StoreLookup[] | undefined) =>
  (rows ?? []).map((row) => ({ value: row.id, label: row.label }));
const currency = (value: unknown) => new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
}).format(Number(value ?? 0));
const display = (value: unknown) => value === null || value === undefined || value === "" ? "—" : String(value);

const ageFromBirthDate = (value?: string) => {
  if (!value) return { years: "0", months: "0", days: "0" };
  const birth = new Date(value);
  const now = new Date();
  if (Number.isNaN(birth.getTime())) return { years: "0", months: "0", days: "0" };
  let years = now.getFullYear() - birth.getFullYear();
  let months = now.getMonth() - birth.getMonth();
  let days = now.getDate() - birth.getDate();
  if (days < 0) {
    months -= 1;
    days += new Date(now.getFullYear(), now.getMonth(), 0).getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  return { years: String(Math.max(0, years)), months: String(months), days: String(days) };
};

const Required = () => <span className="medicine-required">*</span>;

const MedicineLineRow = ({
  line,
  items,
  categories,
  onChange,
  onAdd,
  onRemove,
  removable,
}: {
  line: BillLine;
  items: StoreLookup[] | undefined;
  categories: StoreLookup[] | undefined;
  onChange: (values: Partial<BillLine>) => void;
  onAdd: () => void;
  onRemove: () => void;
  removable: boolean;
}) => {
  const batches = useStoreLookup("batches", { item: line.item }, Boolean(line.item));
  const item = items?.find((entry) => entry.id === line.item);
  const batch = batches.data?.find((entry) => entry.id === line.batch);
  const subUnitsPerUnit = Math.max(1, num(item?.subUnitsPerUnit));
  const quantity = num(line.unitQuantity) * subUnitsPerUnit + num(line.subUnitQuantity);
  const mrp = medicinePrice(batch, item);
  const gross = (quantity / subUnitsPerUnit) * mrp;
  const amount = gross - (gross * num(line.discountPercent)) / 100;
  const filteredItems = line.category
    ? items?.filter((entry) => entry.category === line.category)
    : items;

  useEffect(() => {
    if (!line.item || line.batch || !batches.data?.length) return;
    const firstAvailableBatch = batches.data[0];
    if (!firstAvailableBatch) return;
    onChange({
      batch: firstAvailableBatch.id,
      mrp: String(medicinePrice(firstAvailableBatch, item)),
    });
  }, [batches.data, item?.unitMrp, line.batch, line.item, onChange]);

  return (
    <tr>
      <td>
        <SearchableSelect
          value={line.category}
          options={options(categories)}
          placeholder="Select Category"
          onChange={(category) => onChange({ category, item: "", batch: "", mrp: "0" })}
        />
      </td>
      <td>
        <SearchableSelect
          value={line.item}
          options={options(filteredItems)}
          placeholder="Select Medicine"
          clearable={false}
          onChange={(itemId) => {
            const selected = items?.find((entry) => entry.id === itemId);
            onChange({ item: itemId, category: selected?.category ?? line.category, batch: "", mrp: String(medicinePrice(undefined, selected)) });
          }}
        />
      </td>
      <td><input disabled value={batch?.expiryDate ? formatDate(batch.expiryDate) : ""} /></td>
      <td><input type="number" min="0" value={line.unitQuantity} onChange={(event) => onChange({ unitQuantity: event.target.value })} /></td>
      <td><input disabled value={item?.unitName ?? ""} /></td>
      <td><input type="number" min="0" max={subUnitsPerUnit - 1} value={line.subUnitQuantity} onChange={(event) => onChange({ subUnitQuantity: event.target.value })} /></td>
      <td><input disabled value={item?.subUnitName ?? ""} /></td>
      <td><input disabled value={String(batch?.availableQuantity ?? "")} /></td>
      <td><input disabled value={mrp.toFixed(2)} /></td>
      <td><input type="number" min="0" max="100" step="0.01" value={line.discountPercent} onChange={(event) => onChange({ discountPercent: event.target.value })} /></td>
      <td><input disabled value={Math.max(0, amount).toFixed(2)} /></td>
      <td className="medicine-bill-row-actions">
        {removable ? <button type="button" className="medicine-line-remove" aria-label="Remove medicine" onClick={onRemove}>−</button> : null}
        <button type="button" className="medicine-line-add" aria-label="Add medicine" onClick={onAdd}>+</button>
      </td>
    </tr>
  );
};

export const MedicineBillingPage = () => {
  const { showToast } = useToast();
  const mutation = useCreateStoreRecord();
  const [showForm, setShowForm] = useState(false);
  const bills = useStoreRows("medicine-bills", true);
  const items = useStoreLookup("items");
  const categories = useStoreLookup("masters", { kind: "category" });
  const doctors = useSettingsList("doctors", 1, 100, "", "active");
  const [patientSearch, setPatientSearch] = useState("");
  const patientLookup = usePatientGlobalLookup(usePatientGlobalSearch(patientSearch));
  const [patientId, setPatientId] = useState("");
  const [form, setForm] = useState({
    billDate: localDateTime(),
    consultantDoctor: "",
    patientUhid: "",
    phone: "",
    patientName: "",
    gender: "",
    ageYears: "0",
    ageMonths: "0",
    ageDays: "0",
    address: "",
    state: "",
    district: "",
    pinCode: "",
    discountType: "amount" as "amount" | "percentage",
    discount: "0",
  });
  const [lines, setLines] = useState<BillLine[]>([blankLine()]);
  const [payments, setPayments] = useState<PaymentLine[]>([blankPayment()]);

  const lineTotal = (line: BillLine) => {
    const item = items.data?.find((entry) => entry.id === line.item);
    const subUnits = Math.max(1, num(item?.subUnitsPerUnit));
    const quantity = num(line.unitQuantity) * subUnits + num(line.subUnitQuantity);
    // The selected batch rate is resolved in the row; pharmacy MRP is the item fallback.
    const mrp = num(line.mrp) > 0 ? num(line.mrp) : medicinePrice(undefined, item);
    const gross = (quantity / subUnits) * mrp;
    return Math.max(0, gross - (gross * num(line.discountPercent)) / 100);
  };
  const subTotal = useMemo(() => lines.reduce((sum, line) => sum + lineTotal(line), 0), [items.data, lines]);
  const overallDiscount = form.discountType === "percentage"
    ? (subTotal * Math.min(100, num(form.discount))) / 100
    : Math.min(subTotal, num(form.discount));
  const grandTotal = Math.max(0, subTotal - overallDiscount);
  const totalPay = payments.reduce((sum, payment) => sum + num(payment.amount), 0);
  const totalDue = Math.max(0, grandTotal - totalPay);

  const updateLine = (key: number, values: Partial<BillLine>) =>
    setLines((current) => current.map((line) => line.key === key ? { ...line, ...values } : line));
  const updatePayment = (key: number, values: Partial<PaymentLine>) =>
    setPayments((current) => current.map((payment) => payment.key === key ? { ...payment, ...values } : payment));

  const selectPatient = (id: string) => {
    setPatientId(id);
    if (!id) return;
    const patient = patientLookup.data?.data.find((entry) => entry.id === id);
    if (!patient) return;
    const age = ageFromBirthDate(patient.dateOfBirth);
    setPatientSearch(patient.code ?? patient.label);
    setForm((current) => ({
      ...current,
      patientUhid: patient.code ?? "",
      patientName: patient.label,
      phone: patient.phone ?? "",
      gender: patient.gender ?? "",
      ageYears: age.years,
      ageMonths: age.months,
      ageDays: age.days,
      address: patient.address ?? "",
      state: patient.state ?? "",
      district: patient.district ?? "",
      pinCode: patient.pinCode ?? "",
    }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const invalidLine = lines.some((line) => !line.item || !line.batch || (num(line.unitQuantity) <= 0 && num(line.subUnitQuantity) <= 0));
    if (!form.consultantDoctor || !form.phone.trim() || !form.patientName.trim() || !form.gender || invalidLine) {
      showToast("Complete the required patient, doctor, medicine, batch, and quantity fields.", "error");
      return;
    }
    if (totalPay > grandTotal) {
      showToast("Total payment cannot exceed the grand total.", "error");
      return;
    }
    try {
      const saved = await mutation.mutateAsync({
        resource: "medicine-bills",
        payload: {
          ...form,
          patient: patientId || null,
          patientUhid: form.patientUhid || null,
          ageYears: num(form.ageYears),
          ageMonths: num(form.ageMonths),
          ageDays: num(form.ageDays),
          address: form.address || null,
          state: form.state || null,
          district: form.district || null,
          pinCode: form.pinCode || null,
          discount: num(form.discount),
          lines: lines.map(({ item, batch, unitQuantity, subUnitQuantity, discountPercent }) => ({
            item,
            batch,
            unitQuantity: num(unitQuantity),
            subUnitQuantity: num(subUnitQuantity),
            discountPercent: num(discountPercent),
          })),
          payments: payments.map(({ date, mode, bankName, amount }) => ({
            date,
            mode,
            bankName: bankName || null,
            amount: num(amount),
          })),
        },
      });
      showToast(`Medicine bill ${String(saved.billNumber ?? "")} created successfully.`);
      setShowForm(false);
      setLines([blankLine()]);
      setPayments([blankPayment()]);
      setPatientId("");
      setPatientSearch("");
      setForm((current) => ({ ...current, billDate: localDateTime(), patientUhid: "", phone: "", patientName: "", gender: "", ageYears: "0", ageMonths: "0", ageDays: "0", address: "", state: "", district: "", pinCode: "", discount: "0" }));
    } catch (error) {
      showToast(error instanceof ApiError ? error.message : "Medicine bill could not be created.", "error");
    }
  };

  const doctorOptions = (doctors.data?.data ?? []).map((doctor) => ({
    value: doctor.id,
    label: `${String(doctor.salutation ?? "Dr.")} ${String(doctor.name ?? "")}`.trim(),
  }));

  return (
    <div className="page-stack medicine-billing-page">
      <ModuleHero
        eyebrow="Pharmacy"
        title={showForm ? "Create Medicine Bill" : "Medicine Billing"}
        description="Create patient medicine bills and review previous billing."
        icon={IconPill}
        tone="store"
      />
      {!showForm ? (
        <section className="content-card">
          <div className="section-heading">
            <h2>Medicine Bills List</h2>
            <button className="button button-primary" type="button" onClick={() => setShowForm(true)}>
              + Create Bill
            </button>
          </div>
          {bills.isLoading ? <LoadingState columns={10} /> : null}
          {bills.isError ? (
            <ErrorState
              title="Unable to load medicine bills"
              message="Please try again."
              action={{ label: "Try again", onClick: () => void bills.refetch() }}
            />
          ) : null}
          {bills.data && bills.data.data.length === 0 && !bills.isLoading ? (
            <EmptyState title="No medicine bills found" message="Create a medicine bill to start billing patients." />
          ) : null}
          {bills.data && bills.data.data.length > 0 ? (
            <div className="table-scroll">
              <table className="medicine-bills-list-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Bill Date</th>
                    <th>Bill No</th>
                    <th>UHID</th>
                    <th>Patient Name</th>
                    <th>Phone</th>
                    <th>Consultant Doctor</th>
                    <th>Total</th>
                    <th>Paid</th>
                    <th>Due</th>
                  </tr>
                </thead>
                <tbody>
                  {bills.data.data.map((bill, index) => (
                    <tr key={String(bill.id)}>
                      <td>{index + 1}</td>
                      <td>{bill.billDate ? formatDate(String(bill.billDate)) : "—"}</td>
                      <td><strong>{display(bill.billNumber)}</strong></td>
                      <td>{display(bill.patientUhid)}</td>
                      <td>{display(bill.patientName)}</td>
                      <td>{display(bill.phone)}</td>
                      <td>{display(bill.consultantDoctorName)}</td>
                      <td>{currency(bill.grandTotal)}</td>
                      <td>{currency(bill.totalPayment)}</td>
                      <td>{currency(bill.dueAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}
      {showForm ? (
      <form onSubmit={submit}>
        <section className="content-card medicine-form-heading-card">
          <div className="section-heading">
            <h2>Add Medicine Bill</h2>
            <button className="button button-secondary" type="button" onClick={() => setShowForm(false)}>Back</button>
          </div>
        </section>
        <section className="content-card medicine-patient-card">
          <div className="section-heading">
            <h2>Patient Details</h2>
          </div>
          <div className="medicine-patient-grid">
          <label><span>Date <Required /></span><input type="datetime-local" required value={form.billDate} onChange={(event) => setForm((current) => ({ ...current, billDate: event.target.value }))} /></label>
          <label><span>Consultant Doctor <Required /></span><SearchableSelect value={form.consultantDoctor} options={doctorOptions} placeholder="Select Doctor" clearable={false} onChange={(consultantDoctor) => setForm((current) => ({ ...current, consultantDoctor }))} /></label>
          <label>UHID<PatientUhidAutocomplete id="medicine-bill-uhid" value={patientSearch} selectedId={patientId} options={patientLookup.data?.data} isFetching={patientLookup.isFetching} isError={patientLookup.isError} mode="uhid-name-phone" onInputChange={(value) => { setPatientSearch(value); if (!value) selectPatient(""); }} onChange={selectPatient} /></label>
          <label><span>Phone <Required /></span><input required value={form.phone} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} /></label>
          <label><span>Patient&apos;s Name <Required /></span><input required value={form.patientName} onChange={(event) => setForm((current) => ({ ...current, patientName: event.target.value }))} /></label>
          <label><span>Gender <Required /></span><select required value={form.gender} onChange={(event) => setForm((current) => ({ ...current, gender: event.target.value }))}><option value="">Select Gender</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option><option value="prefer-not-to-say">Prefer not to say</option></select></label>
          <label>Year<input type="number" min="0" max="150" value={form.ageYears} onChange={(event) => setForm((current) => ({ ...current, ageYears: event.target.value }))} /></label>
          <label>Month<input type="number" min="0" max="11" value={form.ageMonths} onChange={(event) => setForm((current) => ({ ...current, ageMonths: event.target.value }))} /></label>
          <label>Day<input type="number" min="0" max="31" value={form.ageDays} onChange={(event) => setForm((current) => ({ ...current, ageDays: event.target.value }))} /></label>
          <label className="medicine-address"><span>Address <Required /></span><input required value={form.address} onChange={(event) => setForm((current) => ({ ...current, address: event.target.value }))} /></label>
          <label><span>State <Required /></span><input required value={form.state} onChange={(event) => setForm((current) => ({ ...current, state: event.target.value }))} /></label>
          <label>District<input value={form.district} onChange={(event) => setForm((current) => ({ ...current, district: event.target.value }))} /></label>
          <label>Pin No.<input value={form.pinCode} onChange={(event) => setForm((current) => ({ ...current, pinCode: event.target.value }))} /></label>
          </div>
        </section>

        <section className="content-card medicine-lines-card">
          <div className="section-heading">
            <h2>Medicine Details</h2>
          </div>
          <div className="table-scroll">
            <table className="store-charge-table medicine-bill-table">
                <thead><tr><th>Med Category</th><th>Med Name <Required /></th><th>Exp Dt</th><th>Unit Qty</th><th>Unit</th><th>Sub Unit Qty</th><th>Sub Unit</th><th>Avl Qty</th><th>MRP</th><th>Dis (%)</th><th>Amount (₹)</th><th>#</th></tr></thead>
              <tbody>{lines.map((line) => <MedicineLineRow key={line.key} line={line} items={items.data} categories={categories.data} onChange={(values) => updateLine(line.key, values)} onAdd={() => setLines((current) => [...current, blankLine()])} onRemove={() => setLines((current) => current.filter((entry) => entry.key !== line.key))} removable={lines.length > 1} />)}</tbody>
            </table>
          </div>
        </section>

        <section className="content-card medicine-settlement-card">
          <div className="section-heading">
            <h2>Payment Details</h2>
          </div>
          <div className="medicine-settlement-layout">
            <div className="medicine-payment-table table-scroll">
              <table className="store-charge-table">
                <thead><tr><th>Date</th><th>Mode</th><th>Bank</th><th>Amount (₹)</th><th>#</th></tr></thead>
                <tbody>{payments.map((payment) => <tr key={payment.key}><td><input type="datetime-local" value={payment.date} onChange={(event) => updatePayment(payment.key, { date: event.target.value })} /></td><td><select value={payment.mode} onChange={(event) => updatePayment(payment.key, { mode: event.target.value as PaymentLine["mode"] })}><option>Cash</option><option>UPI</option><option>Transfer to Bank Account</option><option>Cheque</option><option>Online</option><option>Other</option></select></td><td><input disabled={payment.mode === "Cash"} placeholder={payment.mode === "Cash" ? "--" : "Bank name"} value={payment.bankName} onChange={(event) => updatePayment(payment.key, { bankName: event.target.value })} /></td><td><input type="number" min="0" step="0.01" value={payment.amount} onChange={(event) => updatePayment(payment.key, { amount: event.target.value })} /></td><td><button type="button" className="store-add" aria-label="Add payment" onClick={() => setPayments((current) => [...current, blankPayment()])}>+</button>{payments.length > 1 ? <button type="button" className="store-remove" aria-label="Remove payment" onClick={() => setPayments((current) => current.filter((entry) => entry.key !== payment.key))}>−</button> : null}</td></tr>)}</tbody>
              </table>
            </div>
            <aside className="medicine-bill-summary" aria-label="Bill totals">
              <div><span>Subtotal</span><strong>₹{subTotal.toFixed(2)}</strong></div>
              <div className="medicine-discount-row"><span>Discount</span><label><select aria-label="Discount type" value={form.discountType} onChange={(event) => setForm((current) => ({ ...current, discountType: event.target.value as "amount" | "percentage" }))}><option value="amount">₹</option><option value="percentage">%</option></select><input aria-label="Discount value" type="number" min="0" step="0.01" value={form.discount} onChange={(event) => setForm((current) => ({ ...current, discount: event.target.value }))} /></label></div>
              <div className="medicine-grand-total"><span>Grand Total</span><strong>₹{grandTotal.toFixed(2)}</strong></div>
              <div className="medicine-paid"><span>Total Paid</span><strong>₹{totalPay.toFixed(2)}</strong></div>
              <div className="medicine-due"><span>Total Due</span><strong>₹{totalDue.toFixed(2)}</strong></div>
            </aside>
          </div>
          <div className="medicine-form-actions">
            <button className="button button-primary medicine-save-bill" type="submit" disabled={mutation.isPending}>{mutation.isPending ? "Creating Bill…" : "Create Bill"}</button>
          </div>
        </section>
      </form>
      ) : null}
    </div>
  );
};
